/**
 * OSMDExerciseScene - scene expander (MUS-317, Phase 1 "7b").
 *
 * Expands one playable take of the Exercise Section's `stepwise_run` recipe -
 * (recipe prompt x cell coordinate x frontier configuration) - into BOTH the
 * notation (a MusicXML string the scene feeds to OSMDPlayableScene) and the
 * judged-note stream, from ONE computation, per the MUS-317 consumption
 * contract (documentation/exercise-section/05-content-authoring.md section 1).
 *
 * What this owes (MUS-317):
 * - Interval-walk expansion from the recipe's `intervals` (carried as data, not
 *   hardcoded formulas): the traversal is recipe-driven, and key spelling is
 *   derived from the scale itself (not the figure), so a different figure over the
 *   same scale is a different interval list rather than new expansion code.
 * - Register by the staff: each hand starts in the octave that keeps its run on
 *   its own staff with the fewest ledger lines (within a 61-key keyboard where it
 *   can), the left hand one or two octaves
 *   below the right; past 3 ledger lines the run is written under 8va / 8vb (or
 *   15ma / 15mb) at its sounding pitch (OSMDExerciseScene.runScore.ts). The
 *   figure's register anchor only breaks a tie.
 * - Honest key spelling: notes are spelled by the cell's scale formula in the
 *   tonic's key (F# in G major, never Gb; F natural in D Dorian), under the key
 *   signature the scale's family is written with (scaleNotation.ts: Major its
 *   own, a mode its parent major's, harmonic and melodic minor the tonic's minor
 *   key, every other scale none), or none at all when the user shows
 *   accidentals only. A cell whose tonic label would write a theoretical key is
 *   written from the enharmonic tonic (Db minor as C# minor); its pitches are
 *   the same.
 * - The spelling rides on each pitch's step and alter only. Whether an
 *   accidental is drawn is left to the renderer, which reads the key signature
 *   and the bar (OSMD draws every <accidental> element it is given, even one
 *   the key signature already covers, so none is emitted).
 * - Chord-capable output path: notes carry a midi ARRAY (this figure is melodic,
 *   one pitch per event, but the pipeline never bakes in one-pitch-per-event).
 *
 * - Unmetered: no time signature drawn, no barline but the final double bar,
 *   every note the same value with no rest after the last; beams by the beat.
 * - Finger numbers: the reference fingering (scaleFingering.ts) on every note,
 *   display only, when the user shows them; none where no fingering is authored.
 * No mid-run clef changes are ever emitted.
 *
 * This module is PURE (no Phaser, no OSMD, no React) so the music-theory logic
 * is unit-tested in isolation. The scene binds it to the session machine.
 */

import type { StepwiseRunPrompt } from 'Types/ExerciseRecipeTypes';
import type { Direction, Hand, ScaleCell, ScaleConfig, Tonic } from 'Types/ExerciseContentTypes';
import { unitIdForScaleSlug } from 'Seeds/ExerciseContent/scaleLevels';
import {
  DEFAULT_KEY_SIGNATURE_DISPLAY,
  scaleNotation,
  type KeySignatureDisplay,
} from 'Seeds/ExerciseContent/scaleNotation';
import { spellScale, type NoteLetter, type SpelledNote } from 'Seeds/ExerciseContent/scaleSpelling';
import {
  DEFAULT_FINGER_NUMBERS_DISPLAY,
  fingeringFor,
  runFingers,
  type Finger,
  type FingerNumbersDisplay,
} from 'Seeds/ExerciseContent/scaleFingering';
import {
  RUN_METER,
  chooseRegister,
  staffPosition,
  writeRunScore,
  type RunHand,
  type RunScoreNote,
} from './OSMDExerciseScene.runScore';

/** One expanded, judgeable note of the run (chord-capable via `midiNotes`). */
export interface JudgedNote {
  /** Position in the emitted judged-note stream (staff-flattened). */
  index: number;
  /** The pitch(es) sounding at this note. Melodic here => length 1. */
  midiNotes: number[];
  /** Which hand/staff plays it. */
  hand: 'RH' | 'LH';
  /** Absolute onset in MusicXML input divisions (DIVISIONS=4 per quarter). */
  startDivisions: number;
  /** Note length in the same division space (quarter=4, eighth=2, 16th=1). */
  durationDivisions: number;
  /** The reference finger shown on the note (display only, never graded); none where unknown. */
  finger?: number;
}

/** The single-computation output: notation + the judged stream that mirrors it. */
export interface ExpandedRun {
  /** MusicXML for OSMDPlayableScene to ingest. */
  musicXml: string;
  /** Hidden measures (one per beat) the notation occupies. */
  measureCount: number;
  /** The judged-note stream, ordered by (onset, RH-before-LH). */
  judgedNotes: JudgedNote[];
  /** The meter the transport runs the run in (one beat per hidden measure); never drawn. */
  timeSignature: [number, number];
  /** Each hand's first tonic, as a MIDI pitch. */
  register: { rhBase: number; lhBase: number };
}

/** How the run is drawn: the user's notation display settings. */
export interface ExpanderNotationOptions {
  keySignature?: KeySignatureDisplay;
  fingerNumbers?: FingerNumbersDisplay;
}

/** rhythm_density -> note length in DIVISIONS=4 input space. */
const RHYTHM_DIVISIONS: Record<ScaleConfig['rhythm_density'], number> = {
  quarter: 4,
  eighth: 2,
  sixteenth: 1,
};

const SEMITONES_PER_OCTAVE = 12;

const NATURAL_SEMITONE: Record<NoteLetter, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

const pitchClassOf = (midi: number): number =>
  ((midi % SEMITONES_PER_OCTAVE) + SEMITONES_PER_OCTAVE) % SEMITONES_PER_OCTAVE;

/** Parse a tonic label ('C', 'F#', 'Db', ...) into its pitch class. */
const tonicPitchClass = (tonic: Tonic): number => {
  const letter = tonic[0] as NoteLetter;
  const accidentals = tonic.slice(1);
  const alter = (accidentals.match(/#/g)?.length ?? 0) - (accidentals.match(/b/g)?.length ?? 0);
  return pitchClassOf(NATURAL_SEMITONE[letter] + alter);
};

/** Parse scientific pitch notation ('C4', 'C3', 'F#5') into a MIDI number. */
const parseSpn = (spn: string): number => {
  const match = spn.match(/^([A-G])(#{0,2}|b{0,2})(-?\d+)$/);
  if (!match) {
    throw new Error(`OSMDExerciseScene.expander: unparseable pitch anchor "${spn}"`);
  }
  const [, letter, accidentals, octaveStr] = match;
  const alter = (accidentals.match(/#/g)?.length ?? 0) - (accidentals.match(/b/g)?.length ?? 0);
  const octave = parseInt(octaveStr, 10);
  return (octave + 1) * SEMITONES_PER_OCTAVE + NATURAL_SEMITONE[letter as NoteLetter] + alter;
};

/**
 * The ascending semitone offsets of the walk (relative to the base tonic), for
 * `range` octaves of `stepIntervals`. Ends on the top tonic (offset 12*range).
 */
const ascendingOffsets = (stepIntervals: number[], rangeOctaves: number): number[] => {
  const steps = rangeOctaves * stepIntervals.length;
  return Array.from({ length: steps }).reduce<number[]>(
    (offsets, _unused, k) => [
      ...offsets,
      offsets[offsets.length - 1] + stepIntervals[k % stepIntervals.length],
    ],
    [0],
  );
};

/** The ascending walk's indexes in the configured direction. */
const directedIndexes = (count: number, direction: Direction): number[] => {
  const ascending = Array.from({ length: count }, (_unused, index) => index);
  if (direction === 'asc') return ascending;
  if (direction === 'desc') return [...ascending].reverse();
  // 'both': asc then back down without doubling the top note, ending on the tonic.
  return [...ascending, ...ascending.slice(0, -1).reverse()];
};

const handsToRender = (hand: Hand): RunHand[] => (hand === 'HT' ? ['RH', 'LH'] : [hand]);

/** A spelled pitch: the scale note on that pitch class, or its plain sharp name off the scale. */
const spellingByPitchClass = (notes: readonly SpelledNote[]): Map<number, SpelledNote> =>
  new Map(notes.map((note) => [note.pitchClass, note]));

/**
 * One hand's fingers along the ascending walk (one per note, bottom tonic to top), or null when
 * the reference table has none for that hand. A descending note keeps its ascending finger.
 */
const ascendingFingers = (
  unitId: string,
  tonic: Tonic,
  hand: RunHand,
  rangeOctaves: number,
): Finger[] | null => {
  const octave = fingeringFor(unitId, tonic);
  const fingers = hand === 'RH' ? octave?.rh : octave?.lh;
  return fingers ? runFingers(fingers, hand, rangeOctaves, unitId) : null;
};

/**
 * Expand a `stepwise_run` take into notation + judged stream from one walk.
 */
export const expandStepwiseRun = (
  prompt: StepwiseRunPrompt,
  cell: ScaleCell,
  config: ScaleConfig,
  notationOptions: ExpanderNotationOptions = {},
): ExpandedRun => {
  const anchorSpn = prompt.register.rh_anchor_by_range[config.range_octaves];
  if (anchorSpn == null) {
    throw new Error(
      `OSMDExerciseScene.expander: no register anchor for range ${config.range_octaves}`,
    );
  }
  const unitId = unitIdForScaleSlug(cell.scale);
  const notation = scaleNotation(
    unitId,
    cell.tonic,
    notationOptions.keySignature ?? DEFAULT_KEY_SIGNATURE_DISPLAY,
  );
  const spelling = spellingByPitchClass(spellScale(unitId, notation.tonic));
  const spell = (midi: number): { letter: NoteLetter; alteration: number } => {
    const scaleNote = spelling.get(pitchClassOf(midi));
    if (!scaleNote) {
      throw new Error(`OSMDExerciseScene.expander: ${midi} is not a note of ${cell.scale}`);
    }
    return { letter: scaleNote.letter, alteration: scaleNote.alteration };
  };

  const offsets = ascendingOffsets(prompt.intervals, config.range_octaves);
  const span = offsets[offsets.length - 1];
  const positionsAt = (base: number): number[] =>
    offsets.map((offset) => {
      const { letter, alteration } = spell(base + offset);
      return staffPosition(base + offset, letter, alteration);
    });
  const register = chooseRegister(
    tonicPitchClass(cell.tonic),
    span,
    positionsAt,
    parseSpn(anchorSpn),
    (base) => offsets.map((offset) => base + offset),
  );

  const durationDivisions = RHYTHM_DIVISIONS[config.rhythm_density];
  const hands = handsToRender(config.hand);
  const walk = directedIndexes(offsets.length, config.direction);
  const showFingering =
    (notationOptions.fingerNumbers ?? DEFAULT_FINGER_NUMBERS_DISPLAY) === 'show';

  const handNotes = (hand: RunHand): RunScoreNote[] => {
    const base = hand === 'RH' ? register.rhBase : register.lhBase;
    const fingers = ascendingFingers(unitId, cell.tonic, hand, config.range_octaves);
    return walk.map((ascendingIndex, step) => {
      const midi = base + offsets[ascendingIndex];
      const finger = fingers?.[ascendingIndex];
      return {
        midi,
        ...spell(midi),
        startDivisions: step * durationDivisions,
        durationDivisions,
        ...(finger !== undefined ? { finger } : {}),
      };
    });
  };
  const written = hands.reduce<Partial<Record<RunHand, RunScoreNote[]>>>(
    (all, hand) => ({ ...all, [hand]: handNotes(hand) }),
    {},
  );

  // The judged stream: by onset, the right hand before the left at a shared onset.
  const judgedNotes: JudgedNote[] = walk
    .flatMap((_unused, step) =>
      hands.map((hand) => {
        const note = written[hand]![step];
        return {
          midiNotes: [note.midi],
          hand,
          startDivisions: note.startDivisions,
          durationDivisions: note.durationDivisions,
          ...(note.finger !== undefined ? { finger: note.finger } : {}),
        };
      }),
    )
    .map((note, index) => ({ index, ...note }));

  const score = writeRunScore({
    keySignatureFifths: notation.keySignatureFifths,
    hands: written,
    showFingering,
  });

  return {
    musicXml: score.musicXml,
    measureCount: score.measureCount,
    judgedNotes,
    timeSignature: RUN_METER,
    register,
  };
};
